import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Saveable per-user defaults for the LR Direct ATLAS voice post chips.
 *
 * Stored server-side in profiles.voice_post_defaults (jsonb) so the defaults
 * follow the user across devices, matching the notification-preferences
 * convention on the same table. Only the chips that repeat get defaults —
 * origin/destination/date/weight/rate change every load.
 */
export type VoicePostDefaultKey = "equipment_type" | "post_type" | "routing";

export interface VoicePostDefaults {
  equipment_type?: string | null;
  post_type?: "load" | "quote" | null;
  routing?: "private" | "open" | null;
}

export function useVoicePostDefaults() {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [defaults, setDefaults] = useState<VoicePostDefaults>({});
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!userId) {
      setDefaults({});
      setLoaded(true);
      return;
    }
    setLoaded(false);
    (async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("voice_post_defaults")
        .eq("id", userId)
        .maybeSingle();
      if (cancelled) return;
      if (error) {
        console.error("[voice-defaults] load failed", error);
        setDefaults({});
      } else {
        const raw = (data as any)?.voice_post_defaults;
        setDefaults(raw && typeof raw === "object" ? (raw as VoicePostDefaults) : {});
      }
      setLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  /** Set (value) or clear (null) a single default key. Returns success. */
  const setDefault = useCallback(
    async (key: VoicePostDefaultKey, value: string | null): Promise<boolean> => {
      if (!userId) return false;
      const next: VoicePostDefaults = { ...defaults };
      if (value === null || value === "") delete (next as any)[key];
      else (next as any)[key] = value;

      const prev = defaults;
      setDefaults(next); // optimistic
      const { error } = await supabase
        .from("profiles")
        .update({ voice_post_defaults: next as any })
        .eq("id", userId);
      if (error) {
        console.error("[voice-defaults] save failed", error);
        setDefaults(prev);
        return false;
      }
      return true;
    },
    [defaults, userId],
  );

  return { defaults, loaded, setDefault };
}
